import React from 'react';
import { Link } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import PublicNav from '../components/PublicNav';

function About() {
  const { getAppName } = useConfig();

  return (
    <div className="about-page">
      <PublicNav />

      {/* Hero Section */}
      <section className="about-hero">
        <h1>About LawnTrack</h1>
        <p className="hero-subtitle">
          Built by lawn care professionals, for lawn care professionals
        </p>
      </section>

      {/* Story Section */}
      <section className="about-section">
        <div className="section-container">
          <h2>Our Story</h2>
          <p>
            LawnTrack was born from the real-world challenges of running a lawn care business.
            We understand the juggling act of managing routes, tracking customers, and keeping
            your business profitable during the busy season.
          </p>
          <p>
            Our mission is simple: give lawn care professionals the tools they need to work
            smarter, not harder. No more paper lists, no more forgotten stops, no more
            payment confusion.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="about-section alt-bg">
        <div className="section-container">
          <h2>What Makes Us Different</h2>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">📱</div>
              <h3>Mobile-First Design</h3>
              <p>Built for the field. Works on any device, even without internet.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">⚡</div>
              <h3>One-Tap Completion</h3>
              <p>Complete jobs in seconds with our Service Mode interface.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🗺️</div>
              <h3>Smart Routing</h3>
              <p>Optimize your routes with Google Maps integration.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">💰</div>
              <h3>Payment Tracking</h3>
              <p>Know who paid and who owes at a glance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sister Product Section */}
      <section className="about-section">
        <div className="section-container">
          <h2>Year-Round Coverage</h2>
          <p>
            LawnTrack is part of a family of products designed for seasonal service businesses.
            Our sister product, <a href="https://snowtrack.pro" target="_blank" rel="noopener noreferrer">SnowTrack</a>,
            provides the same powerful features for snow removal during the winter months.
          </p>
          <p>
            One account gives you access to both platforms, so you can manage your business
            year-round with familiar, powerful tools.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <h2>Ready to streamline your lawn care business?</h2>
        <Link to="/register" className="cta-btn">Start Your Free Trial</Link>
        <p className="cta-note">No credit card required</p>
      </section>

      {/* Footer */}
      <footer className="page-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <span className="logo-icon">🌿</span>
            <span>{getAppName()}</span>
          </div>
          <div className="footer-links">
            <Link to="/about">About</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/contact">Contact</Link>
          </div>
          <p className="footer-copy">© {new Date().getFullYear()} {getAppName()}. All rights reserved.</p>
        </div>
      </footer>

      <style>{`
        .about-page {
          min-height: 100vh;
          background: #ffffff;
          padding-top: 64px;
        }

        .logo-icon {
          font-size: 1.5rem;
        }

        /* Hero */
        .about-hero {
          background: linear-gradient(135deg, #4CAF50 0%, #388E3C 100%);
          color: white;
          text-align: center;
          padding: 80px 24px;
        }

        .about-hero h1 {
          font-size: 2.5rem;
          font-weight: 800;
          margin: 0 0 16px;
        }

        .hero-subtitle {
          font-size: 1.25rem;
          opacity: 0.9;
          margin: 0;
        }

        /* Sections */
        .about-section {
          padding: 60px 24px;
        }

        .about-section.alt-bg {
          background: #f9fafb;
        }

        .section-container {
          max-width: 800px;
          margin: 0 auto;
        }

        .section-container h2 {
          font-size: 1.75rem;
          font-weight: 700;
          color: #111827;
          margin: 0 0 24px;
        }

        .section-container p {
          font-size: 1.1rem;
          line-height: 1.7;
          color: #4b5563;
          margin: 0 0 16px;
        }

        .section-container a {
          color: #4CAF50;
          font-weight: 500;
        }

        /* Features Grid */
        .features-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 32px;
          margin-top: 32px;
        }

        .feature-item {
          text-align: center;
        }

        .feature-icon {
          font-size: 2.5rem;
          margin-bottom: 12px;
        }

        .feature-item h3 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #111827;
          margin: 0 0 8px;
        }

        .feature-item p {
          font-size: 0.95rem;
          color: #6b7280;
          margin: 0;
        }

        /* CTA */
        .about-cta {
          background: linear-gradient(135deg, #4CAF50 0%, #388E3C 100%);
          color: white;
          text-align: center;
          padding: 60px 24px;
        }

        .about-cta h2 {
          font-size: 1.75rem;
          font-weight: 700;
          margin: 0 0 24px;
        }

        .cta-btn {
          display: inline-block;
          background: white;
          color: #4CAF50;
          font-weight: 600;
          padding: 14px 32px;
          border-radius: 8px;
          text-decoration: none;
          font-size: 1.1rem;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .cta-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.2);
        }

        .cta-note {
          margin: 16px 0 0;
          font-size: 0.9rem;
          opacity: 0.9;
        }

        /* Footer */
        .page-footer {
          background: #111827;
          color: white;
          padding: 40px 24px;
        }

        .footer-content {
          max-width: 1200px;
          margin: 0 auto;
          text-align: center;
        }

        .footer-brand {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .footer-links {
          display: flex;
          justify-content: center;
          gap: 24px;
          margin-bottom: 16px;
        }

        .footer-links a {
          color: #9ca3af;
          text-decoration: none;
          transition: color 0.2s;
        }

        .footer-links a:hover {
          color: white;
        }

        .footer-copy {
          color: #6b7280;
          font-size: 0.9rem;
          margin: 0;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .about-hero h1 {
            font-size: 2rem;
          }

          .features-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }
        }
      `}</style>
    </div>
  );
}

export default About;
